import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import storeService from '../../services/storeService';
import { FiPlus, FiEdit, FiTrash2 } from 'react-icons/fi';
import { toLocalDateTimeString } from '../../utils/dateUtils';

// --- Reusable Form for Tills/Devices ---
const TillForm = ({ till, onSave, onCancel }) => {
  const [formData, setFormData] = useState({ name: '', tid: '', deviceSerial: '' });

  useEffect(() => {
    if (till) {
      setFormData({
        name: till.name || '',
        tid: till.tid || '',
        deviceSerial: till.deviceSerial || '',
      });
    } else {
      setFormData({ name: '', tid: '', deviceSerial: '' });
    }
  }, [till]);

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  const handleSubmit = (e) => { e.preventDefault(); onSave(formData); };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">Till Name*</label>
        <input name="name" value={formData.name} onChange={handleChange} placeholder="e.g., Front Counter, POS 1" required className="mt-1 p-2 border rounded-md w-full" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700">Terminal ID (TID)</label>
        <input name="tid" value={formData.tid} onChange={handleChange} placeholder="Optional TID" className="mt-1 p-2 border rounded-md w-full" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700">Device Serial Number</label>
        <input name="deviceSerial" value={formData.deviceSerial} onChange={handleChange} placeholder="Optional serial number" className="mt-1 p-2 border rounded-md w-full" />
      </div>
      <div className="flex justify-end space-x-3 pt-4">
        <button type="button" onClick={onCancel} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md">Cancel</button>
        <button type="submit" className="bg-primary text-white px-4 py-2 rounded-md">Save Device</button>
      </div>
    </form>
  );
};


const DevicesSettings = () => {
  const [tills, setTills] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTill, setEditingTill] = useState(null);
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');
  const { id: storeId } = useParams();
  const formatPairingStatus = (status) => {
    const normalized = (status || '').toString().toLowerCase();
    if (normalized === 'paired' || normalized === 'success') {
      return { label: 'Paired', className: 'bg-green-100 text-green-800' };
    }
    if (normalized === 'failed' || normalized === 'error') {
      return { label: 'Failed', className: 'bg-rose-100 text-rose-800' };
    }
    if (normalized === 'pending' || normalized === 'in_progress') {
      return { label: 'Pending', className: 'bg-amber-100 text-amber-800' };
    }
    return { label: 'Not paired', className: 'bg-gray-100 text-gray-800' };
  };
  const formatDisplayStatus = (till) => {
    const status = till?.customerDisplayStatus || (till?.customerDisplaySocketId ? 'paired' : 'unpaired');
    return formatPairingStatus(status);
  };
  const formatPairedAt = (value) => {
    if (!value) return '-';
    return toLocalDateTimeString(value) || '-';
  };
  const formatPairedBy = (user) => {
    if (!user) return '-';
    if (typeof user === 'string') return user;
    if (user.name) return user.name;
    if (user.userId) return user.userId;
    return user._id || '-';
  };

  const fetchData = useCallback(async () => {
    try {
      const data = await storeService.getStoreById(storeId);
      setTills(data.tills || []);
    } catch (err) {
      setError('Failed to fetch devices.');
    } finally {
      setIsLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenModal = (till = null) => {
    setEditingTill(till);
    setModalError('');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setEditingTill(null);
    setModalError('');
    setIsModalOpen(false);
  };

  const handleSave = async (formData) => {
    try {
      if (editingTill) {
        await storeService.updateTill(storeId, editingTill._id, formData);
      } else {
        await storeService.addTill(storeId, formData);
      }
      await fetchData(); // Refetch the list to show changes
      handleCloseModal();
    } catch (err) {
      setModalError(err.response?.data?.message || 'Failed to save device.');
    }
  };

  const handleDelete = async (tillId) => {
    if (window.confirm('Are you sure you want to delete this device?')) {
      try {
        await storeService.deleteTill(storeId, tillId);
        await fetchData(); // Refetch the list
      } catch (err) {
        setError('Failed to delete device.');
      }
    }
  };

  if (isLoading) return <div>Loading devices...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">POS Devices (Tills)</h2>
        <button onClick={() => handleOpenModal()} className="flex items-center bg-primary text-white px-3 py-1 rounded-md text-sm">
          <FiPlus className="mr-1" /> Add Device
        </button>
      </div>
      {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">TID</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Device S/N</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Customer Display</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Payment Terminal</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Paired At</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Paired By</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tills.map((till) => {
              const pairing = formatPairingStatus(till.tyroPairingStatus);
              const displayPairing = formatDisplayStatus(till);
              return (
                <tr key={till._id}>
                  <td className="px-4 py-3">{till.name}</td>
                  <td className="px-4 py-3 font-mono text-sm">{till.tid || '-'}</td>
                  <td className="px-4 py-3 font-mono text-sm">{till.deviceSerial}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${till.status === 'Online' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {till.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${displayPairing.className}`}>
                      {displayPairing.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${pairing.className}`}>
                      {pairing.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatPairedAt(till.tyroPairedAt)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatPairedBy(till.tyroPairedBy)}</td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button onClick={() => handleOpenModal(till)} className="text-indigo-600 hover:text-indigo-900"><FiEdit /></button>
                    <button onClick={() => handleDelete(till._id)} className="text-red-600 hover:text-red-900"><FiTrash2 /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {isModalOpen && 
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-lg font-bold mb-4">{editingTill ? 'Edit Device' : 'Add New Device'}</h2>
            {modalError && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{modalError}</p>}
            <TillForm till={editingTill} onSave={handleSave} onCancel={handleCloseModal} />
          </div>
        </div>
      }
    </div>
  );
};

export default DevicesSettings;
